import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Video, CheckCircle, ArrowLeft, Camera, Youtube, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = "/api";

const RegisterContentCreator = () => {
  const [submitted, setSubmitted] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    creator_name: '',
    contact_name: '',
    email: '',
    phone: '',
    content_type: '',
    description: '',
    years_experience: '',
    specialties: '',
    equipment: '',
    website: '',
    youtube: '',
    instagram: '',
    tiktok: '',
    portfolio_url: '',
    sample_work: '',
    notable_projects: '',
    artists_worked_with: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const cleanForm = {
        ...form,
        years_experience: form.years_experience ? parseInt(form.years_experience) : null,
      };
      
      const response = await axios.post(`${API}/register/content-creator`, cleanForm);
      setRegistrationId(response.data.id);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit registration');
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="creator-registration-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md space-y-6"
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
            <CheckCircle size={40} className="text-primary" />
          </div>
          <h1 className="font-heading text-3xl font-bold">Registration Submitted!</h1>
          <p className="text-muted-foreground">
            Thank you for registering as a content creator with Twin Tube! We'll review your application and get back to you within 3-5 business days.
          </p>
          <p className="text-sm text-muted-foreground">
            Your registration ID: <span className="font-mono text-primary">{registrationId}</span>
          </p>
          <div className="pt-4 space-x-4">
            <Link to="/">
              <Button>Back to Home</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-8" data-testid="register-content-creator-page">
      <Link to="/submit" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft size={16} />
        Back to Get Featured
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="border-border/50">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
              <Video size={32} className="text-white" />
            </div>
            <CardTitle className="font-heading text-2xl">Content Creator Registration</CardTitle>
            <CardDescription>
              Join Twin Tube as a verified content creator. Create videos, photography, podcasts, and more for the Twin Cities music scene.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b border-border pb-2">Basic Information</h3>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="creator_name">Creator/Brand Name *</Label>
                    <Input
                      id="creator_name"
                      value={form.creator_name}
                      onChange={(e) => updateForm('creator_name', e.target.value)}
                      placeholder="Your creator or brand name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name *</Label>
                    <Input
                      id="contact_name"
                      value={form.contact_name}
                      onChange={(e) => updateForm('contact_name', e.target.value)}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => updateForm('email', e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => updateForm('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Content Type */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b border-border pb-2">Content Details</h3>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="content_type">Primary Content Type *</Label>
                    <Select value={form.content_type} onValueChange={(v) => updateForm('content_type', v)} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select content type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Video Production">Video Production</SelectItem>
                        <SelectItem value="Photography">Photography</SelectItem>
                        <SelectItem value="Podcasting">Podcasting</SelectItem>
                        <SelectItem value="Live Streaming">Live Streaming</SelectItem>
                        <SelectItem value="Social Media">Social Media Content</SelectItem>
                        <SelectItem value="Music Journalism">Music Journalism/Blogging</SelectItem>
                        <SelectItem value="Graphic Design">Graphic Design</SelectItem>
                        <SelectItem value="Multi-Media">Multi-Media (Multiple Types)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="years_experience">Years of Experience</Label>
                    <Input
                      id="years_experience"
                      type="number"
                      min="0"
                      value={form.years_experience}
                      onChange={(e) => updateForm('years_experience', e.target.value)}
                      placeholder="e.g., 3"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">About You & Your Work *</Label>
                  <Textarea
                    id="description"
                    value={form.description}
                    onChange={(e) => updateForm('description', e.target.value)}
                    placeholder="Tell us about yourself, your creative style, and what drives your content..."
                    rows={4}
                    required
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="specialties">Specialties</Label>
                    <Input
                      id="specialties"
                      value={form.specialties}
                      onChange={(e) => updateForm('specialties', e.target.value)}
                      placeholder="Music videos, interviews, live streams..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="equipment">Equipment/Tools</Label>
                    <Input
                      id="equipment"
                      value={form.equipment}
                      onChange={(e) => updateForm('equipment', e.target.value)}
                      placeholder="Camera gear, editing software..."
                    />
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b border-border pb-2">Online Presence</h3>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="website" className="flex items-center gap-2">
                      Website/Portfolio
                    </Label>
                    <Input
                      id="website"
                      type="url"
                      value={form.website}
                      onChange={(e) => updateForm('website', e.target.value)}
                      placeholder="https://yoursite.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="youtube" className="flex items-center gap-2">
                      <Youtube size={14} /> YouTube
                    </Label>
                    <Input
                      id="youtube"
                      value={form.youtube}
                      onChange={(e) => updateForm('youtube', e.target.value)}
                      placeholder="YouTube channel URL"
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="instagram" className="flex items-center gap-2">
                      <Instagram size={14} /> Instagram
                    </Label>
                    <Input
                      id="instagram"
                      value={form.instagram}
                      onChange={(e) => updateForm('instagram', e.target.value)}
                      placeholder="@yourusername"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tiktok">TikTok</Label>
                    <Input
                      id="tiktok"
                      value={form.tiktok}
                      onChange={(e) => updateForm('tiktok', e.target.value)}
                      placeholder="@yourusername"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="portfolio_url">Portfolio/Reel URL</Label>
                  <Input
                    id="portfolio_url"
                    type="url"
                    value={form.portfolio_url}
                    onChange={(e) => updateForm('portfolio_url', e.target.value)}
                    placeholder="Link to your portfolio or demo reel"
                  />
                </div>
              </div>

              {/* Portfolio */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b border-border pb-2">Portfolio & Experience</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="sample_work">Sample Work Links</Label>
                  <Textarea
                    id="sample_work"
                    value={form.sample_work}
                    onChange={(e) => updateForm('sample_work', e.target.value)}
                    placeholder="Share links to your best work (one per line)..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notable_projects">Notable Projects</Label>
                  <Textarea
                    id="notable_projects"
                    value={form.notable_projects}
                    onChange={(e) => updateForm('notable_projects', e.target.value)}
                    placeholder="Tell us about your notable projects or collaborations..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="artists_worked_with">Artists/Venues Worked With</Label>
                  <Input
                    id="artists_worked_with"
                    value={form.artists_worked_with}
                    onChange={(e) => updateForm('artists_worked_with', e.target.value)}
                    placeholder="List any Twin Cities artists or venues you've worked with"
                  />
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-2">What happens next?</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Our team will review your application within 3-5 business days</li>
                  <li>We'll verify your portfolio and online presence</li>
                  <li>Once approved, you'll be able to upload content and collaborate with local artists</li>
                  <li>You'll receive a verified creator badge on your profile</li>
                </ul>
              </div>

              <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default RegisterContentCreator;
